import { actions as snackbarActions } from "@reducers/snackbarReducer";
import { actions as authActions } from "@reducers/authReducer";
import { store } from "@store";
import { IDFA } from "@lib/idfa";
import { getUniqueId } from "react-native-device-info";
import getTokenZ from "@utils/getTokenZ";
import { logCurlForResponse, logCurlForError } from "./curlLogger";

const onFulfilledRequestInterceptor = async (config) => {
  try {
    const [token, idfa, unique_device_id] = await Promise.all([
      getTokenZ(),
      IDFA.getIDFA(),
      getUniqueId(),
    ]);

    // Respect a caller-set Authorization (e.g. onboarding screens pass the
    // registration-session UUID via withRegistrationAuth before the leader
    // user_token exists). Only fall back to the logged-in user token when
    // the caller didn't set one.
    if (!config.headers.Authorization) {
      config.headers.Authorization = token;
    }

    config.headers["x-app-idfa"] = idfa || "";

    config.headers["x-app-device-id"] = unique_device_id;

    config.headers["request-startTime"] = new Date().getTime();
    if (__DEV__) {
      const method = (config.method || "GET").toLowerCase();
      const url = `${config.baseURL || ""}/${config.url || ""}`.replace(
        /([^:]\/)\/+/g,
        "$1",
      );
      const mergedHeaders = {
        ...(config.headers?.common || {}),
        ...Object.fromEntries(
          Object.entries(config.headers || {}).filter(
            ([k]) =>
              ![
                "common",
                "delete",
                "get",
                "head",
                "post",
                "put",
                "patch",
                "request-startTime",
              ].includes(k),
          ),
        ),
      };
      const body = config.data;
      const isJsonBody = body && typeof body === "object";
      if (isJsonBody) mergedHeaders["Content-Type"] = "application/json";
      const headerFlags = Object.entries(mergedHeaders)
        .map(([k, v]) => `-H '${k}: ${v}'`)
        .join(" ");
      const bodyFlag = body
        ? `-d '${isJsonBody ? JSON.stringify(body) : body}'`
        : "";
      // eslint-disable-next-line no-console
      console.log(
        `[CURL] curl -X ${method.toUpperCase()} '${url}' ${headerFlags} ${bodyFlag}`.trim(),
      );
    }
  } catch (error) {
    console.error("Error in onFulfilledRequestInterceptor:", error);
  }

  return config;
};

const onFulfilledResponseInterceptor = async (response) => {
  const currentTime = new Date().getTime();
  const startTime = response.config.headers["request-startTime"];
  response.config.headers["request-endTime"] = currentTime;
  response.config.headers["request-duration"] = currentTime - startTime;
  logCurlForResponse(response);
  if (__DEV__) {
    // eslint-disable-next-line no-console
    console.log(
      `[API] ${response.status} ${response.config.url} (${currentTime - startTime}ms)`,
    );
  }
  if (response.data === "SERVER_UNAVAILABLE") {
    const payload = {
      shouldShow: true,
      message: "कुछ देर बाद प्रयास करे",
      bottom: 45,
      position: "bottom",
      autoHide: false,
      actionText: "Okay",
      actionHandler: () => {
        store.dispatch(snackbarActions.hideSnackbar());
      },
    };
    store.dispatch(snackbarActions.showSnackbar(payload));

    setTimeout(() => {
      store.dispatch(snackbarActions.hideSnackbar());
    }, 4000);
  } else {
    const cpDbId = store.getState()?.auth?.user?.cp_db_id;
    if (cpDbId) {
      // KYC-flow face capture is OWNED by the Complete-KYC page's selfie tile
      // (capture-for-result → user_photo upload). Dispatching it here launched
      // the backend-driven auto modal on top of the KYC page (and again over
      // the submit-success modal after the get-kyc refetch), and its in-flight
      // launch made the tile's capture reject with E_ALREADY_RUNNING and fall
      // back to the plain camera. Only LOGIN / DELIVERIES_PAGE configs drive
      // the auto modal.
      const dispatchFaceCapture = (cfg) => {
        if (!cfg || cfg.faceCaptureFlow === "KYC") return;
        store.dispatch(authActions.setFaceCaptureData(cfg));
      };
      if (response?.data?.face_capture) {
        dispatchFaceCapture(response.data.face_capture);
      }
      if (response.headers["x-face-capture"]) {
        try {
          dispatchFaceCapture(JSON.parse(response.headers["x-face-capture"]));
        } catch {
          // malformed header — ignore
        }
      }
    }
    return response;
  }
};

const onRejectedResponseInterceptor = async (error) => {
  logCurlForError(error);
  if (__DEV__) {
    const status = error.response?.status ?? "ERR";
    const url = error.config?.url ?? "";
    // eslint-disable-next-line no-console
    console.log(`[API] ${status} ${url} — ${error.message}`);
  }
  const { data = {} } = error.response || {};
  if (error.code === "ECONNABORTED") {
    const payload = {
      shouldShow: true,
      message: "कुछ देर बाद प्रयास करे",
      bottom: 45,
      position: "bottom",
      autoHide: false,
      actionText: "Okay",
      actionHandler: () => {
        store.dispatch(snackbarActions.hideSnackbar());
      },
    };
    store.dispatch(snackbarActions.showSnackbar(payload));
    setTimeout(() => {
      store.dispatch(snackbarActions.hideSnackbar());
    }, 4000);
  } else if (error.response && error.response.status === 500) {
    const message = "हमारे सर्वर पे कुछ काम चल रहा है। जल्द ही वापस आयेंगे ।";
    const payload = {
      shouldShow: true,
      position: "bottom",
      autoHide: false,
      message,
      actionText: "Okay",
      actionHandler: () => {
        store.dispatch(snackbarActions.hideSnackbar());
      },
    };

    store.dispatch(snackbarActions.showSnackbar(payload));

    setTimeout(() => {
      store.dispatch(snackbarActions.hideSnackbar());
    }, 4000);
  } else if (error.response && error.response.status === 401) {
    store.dispatch(authActions.signOutUser());
    // NavigationService.navigate("AuthStack");
  } else if (
    error.response &&
    (error.response.status === 400 || error.response.status === 409)
  ) {
    // 409 (conflict — e.g. AADHAAR_ALREADY_IN_USE on KYC resubmit) is surfaced
    // like 400: show the server's `message` and fall through to reject. Without
    // this it hit the generic 402–499 branch below, which showed a generic
    // "try again later" AND `return`ed (resolving the promise) — so a rejected
    // request looked SUCCESSFUL to the caller (KYC v2 then showed its success
    // modal and cleared the draft on a duplicate-Aadhaar rejection).
    const { message = "" } = data;

    const payload = {
      shouldShow: true,
      message,
      bottom: 45,
      position: "bottom",
      autoHide: false,
      actionText: "Okay",
      actionHandler: () => {
        store.dispatch(snackbarActions.hideSnackbar());
      },
    };

    if (message) {
      store.dispatch(snackbarActions.showSnackbar(payload));

      setTimeout(() => {
        store.dispatch(snackbarActions.hideSnackbar());
      }, 4000);
    }
  } else if (error.response && error.response.status === 429) {
    const payload = {
      shouldShow: true,
      message: data,
      bottom: 0,
      position: "bottom",
      autoHide: false,
      actionText: "Okay",
      actionHandler: () => {
        store.dispatch(snackbarActions.hideSnackbar());
      },
    };

    if (data) {
      store.dispatch(snackbarActions.showSnackbar(payload));

      setTimeout(() => {
        store.dispatch(snackbarActions.hideSnackbar());
      }, 4000);
    }
  } else if (error.response && error.response.status === 412) {
    return Promise.resolve(error.response);
  } else if (error.response && error.response.status === 418) {
    return Promise.resolve(error.response);
  } else if (
    error.response &&
    error.response.status > 401 &&
    error.response.status < 500
  ) {
    const payload = {
      shouldShow: true,
      message: "कुछ देर बाद प्रयास करे",
      bottom: 45,
      position: "bottom",
      autoHide: false,
      actionText: "Okay",
      actionHandler: () => {
        store.dispatch(snackbarActions.hideSnackbar());
      },
    };

    store.dispatch(snackbarActions.showSnackbar(payload));

    setTimeout(() => {
      store.dispatch(snackbarActions.hideSnackbar());
    }, 4000);
    return;
  } else {
    console.error(error);
  }
  return Promise.reject(error);
};

export {
  onFulfilledRequestInterceptor,
  onFulfilledResponseInterceptor,
  onRejectedResponseInterceptor,
};
